import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import Button from 'components/field/Button';
import * as S from 'components/field/styled/Chips.styled';
import Icon from 'components/global/Icon';
import Typography from 'components/global/Typography';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';

import { ADDRESS } from 'services/consts';

import AppLogger from 'utils/AppLogger';

import { faCheck, faPlus, faXmark } from '@fortawesome/pro-solid-svg-icons';

const TagChip = ({ chip, handleDelete, viewMode, autoTranslation = true }) => (
	<S.Token key={chip.id}>
		<Typography autoTranslation={autoTranslation}>{chip.tag}</Typography>
		<Icon
			icon={viewMode ? faCheck : faXmark}
			onClick={(e) => {
				if (viewMode) return;
				handleDelete(e, chip);
			}}
		/>
	</S.Token>
);

export const TagChips = ({ tags, setTags, parentID, type, isLoading, disabled, autoTranslation = true }) => {
	const [inputValue, setInputValue] = useState('');
	const inputRef = useRef(null);
	const wrapperRef = useRef(null);
	const axios = useAxiosPrivate();
	const { showError, showSuccess } = useAlert();
	const typeMap = useMemo(() => {
		const typeKeys = {
			country: 'countryID',
			state: 'stateID',
			governorate: 'governorateID',
			city: 'cityID',
			district: 'districtID',
			neighbourhood: 'neighbourhoodID'
		};
		return typeKeys[type];
	}, [type]);

	const handleDelete = useCallback(
		(e, item) => {
			e.stopPropagation();
			axios
				.post(ADDRESS.REMOVE_TAG, { id: item.id, type })
				.then(() => {
					setTags((prev) => prev.filter((tag) => tag.id !== item.id));
					showSuccess({
						message: 'Remove tag successfully'
					});
				})
				.catch((error) => {
					showError({
						message: 'Remove tag failed'
					});
					AppLogger.error(error);
				});
		},
		[type]
	);

	const handleAdd = useCallback(
		async (tag) => {
			const isExist = tags.find((item) => item.tag === tag);
			if (isExist) {
				showError({
					message: 'Tag already exists'
				});
				return;
			}

			await axios
				.post(ADDRESS.ADD_TAG, {
					type,
					tag,
					[typeMap]: parentID
				})
				.then((response) => {
					const newTag = { id: response.data.id, tag: response.data.tag };
					setTags((prev) => [...prev, newTag]);
					showSuccess({
						message: 'Add tag successfully'
					});
				})
				.catch((error) => {
					showError({
						message: 'Add tag failed'
					});
					AppLogger.error(error);
				});
		},
		[type, parentID, typeMap, tags]
	);

	const handleChange = (e) => {
		setInputValue(e.target.value);
	};

	const handleKeyDown = (e) => {
		const inputVal = inputValue.trim();
		if (e.key === 'Enter' && inputVal) {
			handleAdd(inputVal);
			setInputValue('');
		}
	};

	const handlePaste = (event) => {
		const clipboardData = event.clipboardData.getData('text');
		if (!clipboardData) return;
		const value = clipboardData.trim();
		handleAdd(value);
		event.preventDefault();
	};

	const handleFocus = useCallback(() => {
		if (!inputRef?.current) return;

		inputRef.current.focus();
	}, [inputRef]);

	const handleBlur = useCallback(() => {
		if (!inputRef?.current) return;
		inputRef.current.blur();
	}, [inputRef]);

	const handleAddButton = () => {
		const inputVal = inputValue.trim();

		if (inputVal) {
			handleAdd(inputValue.trim());
			setInputValue('');
		}
	};

	useEffect(() => {
		if (inputRef.current) {
			handleBlur();
		}
	}, [disabled]);

	return (
		<S.Container>
			<S.InputWrapper>
				<S.Wrapper
					ref={wrapperRef}
					onClick={handleFocus}
					style={{
						...(disabled && {
							backgroundColor: 'var(--background-3)'
						})
					}}>
					{tags?.map((chip) => (
						<TagChip
							key={chip.id}
							chip={chip}
							handleDelete={handleDelete}
							viewMode={disabled}
							autoTranslation={autoTranslation}
						/>
					))}
					{!disabled && (
						<S.InputToken>
							<S.Input
								autoFocus
								onPaste={handlePaste}
								ref={inputRef}
								type="text"
								value={inputValue}
								onChange={handleChange}
								onKeyDown={handleKeyDown}
								disabled={disabled || isLoading}
							/>
						</S.InputToken>
					)}
				</S.Wrapper>
				{!disabled && (
					<Button
						icon={faPlus}
						onClick={() => {
							if (disabled || isLoading) return;

							handleAddButton();
						}}
						variant="borderless"
						css={S.AddButtonCss}
					/>
				)}
			</S.InputWrapper>
		</S.Container>
	);
};

export default memo(TagChips);
